import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

/* ── Shared Tiny Components ── */

const StatusBadge = ({ status }) => {
  const colors = { approved: '#28a745', pending: '#e67e22', rejected: '#dc3545' };
  return (
    <span data-testid={`status-${status}`} style={{
      display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '1rem',
      fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
      background: `${colors[status] || '#999'}18`, color: colors[status] || '#999',
    }}>{status || 'unknown'}</span>
  );
};

const RoleBadge = ({ role }) => (
  <span style={{
    display: 'inline-block', padding: '0.2rem 0.55rem', borderRadius: '1rem',
    fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
    background: role === 'admin' ? 'rgba(26,122,76,0.12)' : 'rgba(100,100,100,0.1)',
    color: role === 'admin' ? 'var(--brand)' : 'var(--muted)',
  }}>{role || 'member'}</span>
);

/* ── Members Table ── */

const MemberTable = ({ users, onApprove, onReject, onPromote, onDemote, onDelete, emptyMsg }) => {
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  if (users.length === 0) {
    return (
      <div data-testid="empty-table-msg" style={{
        padding: '3rem 1.5rem', textAlign: 'center', background: 'var(--surface)',
        borderRadius: 'var(--radius)', border: '1px solid var(--border)',
      }}>
        <i className="fas fa-users" style={{ fontSize: '2rem', color: 'var(--border)', marginBottom: '0.75rem', display: 'block' }}></i>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>{emptyMsg || 'No members found.'}</p>
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 'var(--radius)', overflow: 'auto', border: '1px solid var(--border)' }}>
      <table className="admin-table" data-testid="members-table" style={{ margin: 0 }}>
        <thead><tr style={{ background: 'var(--surface)' }}>
          <th>Name</th><th>Email</th><th>Status</th><th>Role</th><th>Affiliation</th><th>Joined</th><th style={{ width: '180px' }}>Actions</th>
        </tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td style={{ fontWeight: 500 }}>{u.greeting ? `${u.greeting} ` : ''}{u.academic_title && u.academic_title !== 'Non disclosed' ? `${u.academic_title} ` : ''}{u.name}</td>
              <td style={{ fontSize: '0.85rem' }}>{u.email}</td>
              <td><StatusBadge status={u.status} /></td>
              <td><RoleBadge role={u.role} /></td>
              <td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{u.primary_affiliation || '—'}{u.primary_position ? ` · ${u.primary_position}` : ''}</td>
              <td style={{ fontSize: '0.8rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              <td>
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                  {u.status === 'pending' && (<>
                    <button className="btn btn-small" style={{ background: 'var(--success)', fontSize: '0.75rem' }} onClick={() => onApprove(u.id)} data-testid={`approve-${u.id}`}>Approve</button>
                    {rejectingId === u.id ? (
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <input placeholder="Reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.5rem', width: '100px', borderRadius: '4px', border: '1px solid var(--border)' }} data-testid="reject-reason-input" />
                        <button className="btn btn-small btn-danger" style={{ fontSize: '0.75rem' }} onClick={() => { onReject(u.id, rejectReason); setRejectingId(null); setRejectReason(''); }} data-testid={`confirm-reject-${u.id}`}>OK</button>
                        <button className="btn btn-small btn-secondary" style={{ fontSize: '0.75rem' }} onClick={() => { setRejectingId(null); setRejectReason(''); }}>X</button>
                      </div>
                    ) : (
                      <button className="btn btn-small btn-danger" style={{ fontSize: '0.75rem' }} onClick={() => setRejectingId(u.id)} data-testid={`reject-${u.id}`}>Reject</button>
                    )}
                  </>)}
                  {u.status === 'rejected' && (
                    <button className="btn btn-small" style={{ background: 'var(--success)', fontSize: '0.75rem' }} onClick={() => onApprove(u.id)} data-testid={`reapprove-${u.id}`}>Approve</button>
                  )}
                  {u.role === 'admin' ? (
                    <button className="btn btn-small btn-secondary" style={{ fontSize: '0.75rem' }} onClick={() => onDemote(u.id)} data-testid={`demote-${u.id}`}>Demote</button>
                  ) : (
                    <button className="btn btn-small" style={{ fontSize: '0.75rem' }} onClick={() => onPromote(u.id)} data-testid={`promote-${u.id}`}>Admin</button>
                  )}
                  <button className="btn btn-small btn-danger" style={{ fontSize: '0.75rem' }} onClick={() => onDelete(u.id, u.name)} data-testid={`delete-${u.id}`}><i className="fas fa-trash-alt"></i></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Log Trail Module ── */

const formatDuration = (totalSec) => {
  if (!totalSec || totalSec < 1) return '< 1s';
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
};

const LogTrailView = ({ token, API }) => {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTab, setLogTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [summaryRes, logsRes] = await Promise.all([
          axios.get(`${API}/admin/activity-summary`, { headers }),
          axios.get(`${API}/admin/activity-logs?limit=300`, { headers }),
        ]);
        setSummary(summaryRes.data);
        setLogs(logsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchAll();
  }, [token, API]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}><i className="fas fa-spinner fa-spin"></i> Loading activity data...</div>;

  const tabs = [
    { key: 'overview', label: 'User Overview', icon: 'fa-chart-bar' },
    { key: 'logins', label: 'Login History', icon: 'fa-sign-in-alt' },
    { key: 'pages', label: 'Page Visits', icon: 'fa-file-alt' },
    { key: 'timeline', label: 'Live Timeline', icon: 'fa-stream' },
  ];

  const statCards = summary ? [
    { label: 'Total Logins', value: summary.total_logins, icon: 'fa-sign-in-alt', color: '#007bff' },
    { label: 'Page Visits', value: summary.total_page_visits, icon: 'fa-eye', color: '#28a745' },
    { label: 'Unique Users', value: summary.login_summary.length, icon: 'fa-users', color: '#6f42c1' },
    { label: 'Total Events', value: summary.total_logs, icon: 'fa-database', color: '#e67e22' },
  ] : [];

  // Filter data for selected user
  const filteredLogs = selectedUser ? logs.filter(l => l.user_email === selectedUser) : logs;
  const filteredVisits = selectedUser
    ? (summary?.visit_details || []).filter(v => v.user_email === selectedUser)
    : (summary?.visit_details || []);

  return (
    <div data-testid="log-trail-view">
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {statCards.map(s => (
          <div key={s.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
          }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={`fas ${s.icon}`} style={{ color: s.color, fontSize: '0.9rem' }}></i>
            </div>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fg)' }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* User filter */}
      {selectedUser && (
        <div data-testid="user-filter-active" style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem',
          padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)',
          background: 'rgba(26,122,76,0.06)', border: '1px solid rgba(26,122,76,0.15)',
        }}>
          <i className="fas fa-filter" style={{ color: 'var(--brand)', fontSize: '0.75rem' }}></i>
          <span style={{ fontSize: '0.82rem' }}>Filtering by: <strong>{selectedUser}</strong></span>
          <button onClick={() => setSelectedUser(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            <i className="fas fa-times"></i> Clear
          </button>
        </div>
      )}

      {/* Sub tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} data-testid={`log-tab-${t.key}`}
            onClick={() => setLogTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem',
              borderRadius: 'var(--radius)', border: logTab === t.key ? '2px solid var(--brand)' : '2px solid var(--border)',
              background: logTab === t.key ? 'rgba(26,122,76,0.06)' : 'var(--surface)',
              color: logTab === t.key ? 'var(--brand)' : 'var(--muted)',
              cursor: 'pointer', fontSize: '0.82rem', fontWeight: logTab === t.key ? 600 : 400,
            }}>
            <i className={`fas ${t.icon}`} style={{ fontSize: '0.72rem' }}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {logTab === 'overview' && (
        <div data-testid="log-overview">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem' }}></i>
            Time spent per user on the portal. Click a user to filter all tabs.
          </p>
          <div style={{ borderRadius: 'var(--radius)', overflow: 'auto', border: '1px solid var(--border)' }}>
            <table className="admin-table" data-testid="user-overview-table" style={{ margin: 0 }}>
              <thead><tr style={{ background: 'var(--surface)' }}>
                <th>User</th><th>Email</th><th>Total Time</th><th>Page Visits</th><th>Logins</th><th>Last Login</th>
              </tr></thead>
              <tbody>
                {(summary?.user_time || []).map(u => {
                  const loginInfo = (summary?.login_summary || []).find(l => l.user_email === u.user_email);
                  return (
                    <tr key={u.user_email} style={{ cursor: 'pointer', background: selectedUser === u.user_email ? 'rgba(26,122,76,0.04)' : '' }}
                      onClick={() => setSelectedUser(selectedUser === u.user_email ? null : u.user_email)}
                      data-testid={`user-row-${u.user_email}`}>
                      <td style={{ fontWeight: 500 }}>{u.user_name || '—'}</td>
                      <td style={{ fontSize: '0.85rem' }}>{u.user_email}</td>
                      <td style={{ fontWeight: 600, color: 'var(--brand)' }}>{formatDuration(u.total_time_seconds)}</td>
                      <td>{u.total_page_visits}</td>
                      <td>{loginInfo?.login_count || 0}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{loginInfo?.last_login ? new Date(loginInfo.last_login).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
                {(!summary?.user_time || summary.user_time.length === 0) && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No activity data yet. Users will appear here as they browse the portal.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logTab === 'logins' && (
        <div data-testid="log-logins">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem' }}></i>
            Login events sorted by most recent. Click a row to filter by user.
          </p>
          <div style={{ borderRadius: 'var(--radius)', overflow: 'auto', border: '1px solid var(--border)' }}>
            <table className="admin-table" data-testid="login-history-table" style={{ margin: 0 }}>
              <thead><tr style={{ background: 'var(--surface)' }}>
                <th>User</th><th>Email</th><th>Login Count</th><th>Last Login</th>
              </tr></thead>
              <tbody>
                {(selectedUser
                  ? (summary?.login_summary || []).filter(l => l.user_email === selectedUser)
                  : (summary?.login_summary || [])
                ).map(l => (
                  <tr key={l.user_email} style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedUser(selectedUser === l.user_email ? null : l.user_email)}>
                    <td style={{ fontWeight: 500 }}>{l.user_name || '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{l.user_email}</td>
                    <td><span style={{ background: 'rgba(0,123,255,0.08)', color: '#007bff', padding: '0.15rem 0.5rem', borderRadius: '1rem', fontSize: '0.8rem', fontWeight: 600 }}>{l.login_count}</span></td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{new Date(l.last_login).toLocaleString()}</td>
                  </tr>
                ))}
                {(summary?.login_summary || []).length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No login events recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logTab === 'pages' && (
        <div data-testid="log-pages">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem' }}></i>
            Pages visited by users with total time spent on each page.
          </p>
          <div style={{ borderRadius: 'var(--radius)', overflow: 'auto', border: '1px solid var(--border)' }}>
            <table className="admin-table" data-testid="page-visits-table" style={{ margin: 0 }}>
              <thead><tr style={{ background: 'var(--surface)' }}>
                <th>User</th><th>Page</th><th>Visits</th><th>Total Time</th>
              </tr></thead>
              <tbody>
                {filteredVisits.map((v, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>
                      <span style={{ cursor: 'pointer' }} onClick={() => setSelectedUser(selectedUser === v.user_email ? null : v.user_email)}>
                        {v.user_name || v.user_email}
                      </span>
                    </td>
                    <td><code style={{ fontSize: '0.82rem', background: 'var(--surface)', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>{v.page}</code></td>
                    <td>{v.visit_count}</td>
                    <td style={{ fontWeight: 600, color: 'var(--brand)' }}>{formatDuration(v.total_duration)}</td>
                  </tr>
                ))}
                {filteredVisits.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>No page visit data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logTab === 'timeline' && (
        <div data-testid="log-timeline">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.4rem' }}></i>
            Most recent activity events across all users.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {filteredLogs.slice(0, 100).map((log, i) => (
              <div key={i} data-testid={`timeline-event-${i}`} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                padding: '0.6rem 0.85rem', borderRadius: 'var(--radius)',
                border: '1px solid var(--border)', background: 'var(--surface)',
              }}>
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                  background: log.event === 'login' ? 'rgba(0,123,255,0.1)' : 'rgba(40,167,69,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className={`fas ${log.event === 'login' ? 'fa-sign-in-alt' : 'fa-eye'}`}
                    style={{ fontSize: '0.7rem', color: log.event === 'login' ? '#007bff' : '#28a745' }}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                      onClick={() => setSelectedUser(selectedUser === log.user_email ? null : log.user_email)}>
                      {log.user_name || log.user_email}
                    </span>
                    <span style={{
                      fontSize: '0.72rem', padding: '0.1rem 0.4rem', borderRadius: '1rem', fontWeight: 600,
                      background: log.event === 'login' ? 'rgba(0,123,255,0.08)' : 'rgba(40,167,69,0.08)',
                      color: log.event === 'login' ? '#007bff' : '#28a745',
                    }}>{log.event === 'login' ? 'Logged in' : 'Visited page'}</span>
                    {log.page && log.event !== 'login' && (
                      <code style={{ fontSize: '0.78rem', background: 'rgba(0,0,0,0.04)', padding: '0.1rem 0.35rem', borderRadius: '3px' }}>{log.page}</code>
                    )}
                    {log.duration_seconds > 0 && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--brand)' }}>{formatDuration(log.duration_seconds)}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.15rem' }}>
                    {new Date(log.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
            {filteredLogs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                No activity events recorded yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main Admin Dashboard ── */

const AdminDashboard = () => {
  const { token, isAdmin, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [activeModule, setActiveModule] = useState('members');
  const [memberTab, setMemberTab] = useState('approved');

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users?limit=500`, { headers }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, API]);

  useEffect(() => { if (isAdmin) fetchData(); }, [isAdmin, fetchData]);

  const showMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 4000); };

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, { action: 'approve' }, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Member approved successfully'); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || 'Action failed'); }
  };
  const rejectUser = async (userId, reason) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, { action: 'reject', reason }, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Member rejected'); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || 'Action failed'); }
  };
  const promoteUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Promoted to admin'); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || 'Action failed'); }
  };
  const demoteUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=member`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Demoted to member'); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || 'Action failed'); }
  };
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete member "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('Member deleted'); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || 'Action failed'); }
  };

  if (!isAdmin) {
    return (
      <div data-testid="admin-access-denied" style={{ textAlign: 'center', padding: '5rem 1rem' }}>
        <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--border)', marginBottom: '1rem', display: 'block' }}></i>
        <h2 style={{ marginBottom: '0.5rem' }}>Access Denied</h2>
        <p style={{ color: 'var(--muted)' }}>You must be signed in as an administrator to access this page.</p>
      </div>
    );
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '5rem' }}>
      <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', color: 'var(--brand)', marginBottom: '1rem', display: 'block' }}></i>Loading dashboard...
    </div>
  );

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const approvedUsers = users.filter(u => u.status === 'approved');
  const newMembers = approvedUsers.filter(u => u.created_at && new Date(u.created_at) >= thirtyDaysAgo);
  const pendingUsers = users.filter(u => u.status === 'pending');
  const rejectedUsers = users.filter(u => u.status === 'rejected');

  const memberTabs = [
    { key: 'approved', label: 'Approved Members', icon: 'fa-user-check', count: approvedUsers.length, color: '#28a745' },
    { key: 'new', label: 'New Members', icon: 'fa-user-plus', count: newMembers.length, color: '#007bff' },
    { key: 'pending', label: 'Pending Requests', icon: 'fa-user-clock', count: pendingUsers.length, color: '#e67e22' },
    { key: 'rejected', label: 'Rejected Requests', icon: 'fa-user-times', count: rejectedUsers.length, color: '#dc3545' },
  ];

  const currentUsers = memberTab === 'approved' ? approvedUsers
    : memberTab === 'new' ? newMembers
    : memberTab === 'pending' ? pendingUsers : rejectedUsers;

  const emptyMessages = {
    approved: 'No approved members yet.',
    new: 'No new members in the last 30 days.',
    pending: 'No pending requests. All caught up!',
    rejected: 'No rejected requests.',
  };

  const modules = [
    { key: 'members', label: 'Members Management', icon: 'fa-users-cog' },
    { key: 'logtrail', label: 'Log Trail', icon: 'fa-shoe-prints' },
  ];

  return (
    <div data-testid="admin-dashboard" style={{ padding: '0.5rem 0' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--brand)', fontWeight: 600, marginBottom: '0.25rem' }}>Administration</p>
        <h1 data-testid="admin-title" style={{ margin: '0 0 0.25rem' }}>Admin Dashboard</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Manage members, monitor activity, and administer platform resources</p>
      </div>

      {actionMsg && (
        <div data-testid="admin-action-msg" style={{
          padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: 'var(--radius)',
          background: 'rgba(40,167,69,0.08)', border: '1px solid rgba(40,167,69,0.2)', color: '#28a745',
          fontSize: '0.85rem', fontWeight: 500,
        }}>
          <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }}></i>{actionMsg}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1.5rem', minHeight: '500px' }}>
        {/* Sidebar */}
        <aside data-testid="admin-sidebar" style={{
          background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          padding: '1rem 0', height: 'fit-content',
        }}>
          <p style={{ padding: '0 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.5rem' }}>Modules</p>
          {modules.map(m => (
            <button key={m.key} data-testid={`module-${m.key}`}
              onClick={() => setActiveModule(m.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%',
                padding: '0.7rem 1rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                fontWeight: activeModule === m.key ? 600 : 400, textAlign: 'left',
                background: activeModule === m.key ? 'rgba(26,122,76,0.08)' : 'transparent',
                color: activeModule === m.key ? 'var(--brand)' : 'var(--fg)',
                borderLeft: activeModule === m.key ? '3px solid var(--brand)' : '3px solid transparent',
                transition: 'all 0.15s',
              }}>
              <i className={`fas ${m.icon}`} style={{ width: '16px', textAlign: 'center' }}></i>{m.label}
            </button>
          ))}

          {stats && (
            <div style={{ padding: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border)' }}>
              <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.6rem' }}>Overview</p>
              {[
                { label: 'Total Users', val: stats.total_users, icon: 'fa-users' },
                { label: 'Documents', val: stats.total_documents, icon: 'fa-file-alt' },
                { label: 'Events', val: stats.total_events, icon: 'fa-calendar' },
                { label: 'Posts', val: stats.total_posts, icon: 'fa-newspaper' },
              ].map(s => (
                <div key={s.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.35rem 0', fontSize: '0.8rem', color: 'var(--muted)',
                }}>
                  <span><i className={`fas ${s.icon}`} style={{ width: '14px', marginRight: '0.4rem', fontSize: '0.7rem' }}></i>{s.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--fg)' }}>{s.val}</span>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main data-testid="admin-main-content">
          {activeModule === 'members' && (
            <div>
              <div data-testid="member-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                {memberTabs.map(tab => (
                  <button key={tab.key} data-testid={`member-tab-${tab.key}`}
                    onClick={() => setMemberTab(tab.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.6rem 1rem', borderRadius: 'var(--radius)',
                      border: memberTab === tab.key ? `2px solid ${tab.color}` : '2px solid var(--border)',
                      background: memberTab === tab.key ? `${tab.color}0a` : 'var(--surface)',
                      color: memberTab === tab.key ? tab.color : 'var(--muted)',
                      cursor: 'pointer', fontSize: '0.82rem', fontWeight: memberTab === tab.key ? 600 : 400,
                    }}>
                    <i className={`fas ${tab.icon}`} style={{ fontSize: '0.75rem' }}></i>{tab.label}
                    <span style={{
                      background: memberTab === tab.key ? tab.color : 'var(--border)',
                      color: memberTab === tab.key ? '#fff' : 'var(--muted)',
                      padding: '0.1rem 0.45rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700,
                      minWidth: '20px', textAlign: 'center',
                    }}>{tab.count}</span>
                  </button>
                ))}
              </div>
              <MemberTable users={currentUsers} onApprove={approveUser} onReject={rejectUser}
                onPromote={promoteUser} onDemote={demoteUser} onDelete={deleteUser}
                emptyMsg={emptyMessages[memberTab]} />
            </div>
          )}

          {activeModule === 'logtrail' && (
            <LogTrailView token={token} API={API} />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
